import { useState, useEffect } from "react";
import axios from "axios";
import OrdableBranchKey from "./OrdableBranchKey";
import RegenerateAPIKey from "./RegenerateAPIKey";
import PermissionGate from "../layout/PermissionGate";

const OrdableBranchKeys = ({ id }) => {
    const [keys, setKeys] = useState([]);
    const [regenerate, setRegenerate] = useState(false);
    const [branchId, setBranchId] = useState(null);
    const token = localStorage.getItem("token");

    const fetchOrdableKeys = async () => {
        axios
            .get(
                process.env.REACT_APP_API_ENDPOINT +
                `ordable/branch-key/list?merchantId=${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            )
            .then((res) => {
                if (res.data.code === 1) {
                    setKeys(res.data.data);
                }
            })
            .catch(console.error);
    };

    const regenerateOrdableKey = async (branchId) => {
        axios
            .patch(
                process.env.REACT_APP_API_ENDPOINT +
                `ordable/branch-key/regenerate?branchId=${branchId}`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            )
            .then((res) => {
                if (res.data.code === 1) {
                    fetchOrdableKeys();
                }
            })
            .catch(console.error);
    };

    const downloadOrdableKeys = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_ENDPOINT}ordable/branch-key/download?merchantId=${id}`,
                {
                    responseType: "blob",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            const timestamp = Date.now();
            const filename = `ordable_keys_${timestamp}.xlsx`;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error("Export failed:", err);
        }
    };

    useEffect(() => {
        fetchOrdableKeys();
    }, [id]);

    const missingLocation = keys.filter((k) => k?.hasLocation === false).length;

    return (
        <>
            <div className="row g-5 g-lg-10 mt-4">
                {/*begin::Col*/}
                <div className="col p-0 mb-xl-10">
                    <div className="d-flex justify-content-between align-items-center text-dark fw-bold m-0 fs-3 mb-5">
                        <h1 className="d-flex text-dark fw-bold m-0 fs-3 mb-5">
                            Ordable Branch Keys
                        </h1>
                        <PermissionGate
                            required={`download_ordable_branch_keys`}
                            fallback={<></>}
                        >
                            <button onClick={downloadOrdableKeys} className="btn btn-primary">
                                <i className="bi-duotone bi-download mb-1"></i>
                                Download All
                            </button>
                        </PermissionGate>
                    </div>
                    {/*begin::Tables Widget*/}
                    <PermissionGate required={`view_ordable_branch_keys`}>
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center border border-dashed border-gray-300 rounded p-4 mb-6">
                                    <i className="bi-duotone bi-info-circle fs-2 me-3 text-gray-600"></i>
                                    <div>
                                        <div className="fw-bold text-gray-800">
                                            Paste this key into the Ordable store for that branch
                                        </div>
                                        <div className="text-gray-600 fs-7">
                                            This key is only for the Ordable order import. It is not
                                            the same as the Branch API Key above, which belongs to the
                                            merchant v1 API. Changing one never affects the other.
                                        </div>
                                    </div>
                                </div>

                                {missingLocation > 0 && (
                                    <div className="d-flex align-items-center border border-warning rounded p-4 mb-6">
                                        <i className="bi-duotone bi-exclamation-triangle fs-2 me-3 text-warning"></i>
                                        <div>
                                            <div className="fw-bold text-gray-800">
                                                {missingLocation} branch(es) have no saved location
                                            </div>
                                            <div className="text-gray-600 fs-7">
                                                Set the branch coordinates before switching that store to
                                                its Ordable key, otherwise its orders will be rejected.
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {keys?.length === 0 ? (
                                    <div className="text-center w-100">
                                        <img
                                            className="w-25 mx-auto py-20"
                                            src={`${process.env.REACT_APP_PUBLIC_URL}assets/media/misc/no-content.png`}
                                            alt="no-content"
                                        />
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table align-middle table-row-dashed fs-6 gy-5">
                                            <thead>
                                                <tr className="text-muted fw-bold fs-7 text-uppercase gs-0">
                                                    <th className="text-center min-w-120px">
                                                        Branch Name
                                                    </th>
                                                    <th className="text-center min-w-120px">
                                                        Ordable Store Id
                                                    </th>
                                                    <th className="text-center min-w-100px">Location</th>
                                                    <th className="text-center min-w-120px">
                                                        Ordable Key
                                                    </th>
                                                    <th className="text-center min-w-120px">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="text-gray-600 fw-semibold">
                                                {keys &&
                                                    keys.map((key, index) => {
                                                        return (
                                                            <OrdableBranchKey
                                                                item={key}
                                                                key={index}
                                                                setRegenerate={setRegenerate}
                                                                setBranchId={setBranchId}
                                                            />
                                                        );
                                                    })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                            {/*begin::Body*/}
                        </div>
                    </PermissionGate>
                    {/*end::Tables Widget*/}
                </div>
                {/*end::Col*/}
            </div>
            {regenerate && (
                <RegenerateAPIKey
                    regenerate={regenerate}
                    setRegenerate={setRegenerate}
                    regenerateAPIkey={regenerateOrdableKey}
                    branchId={branchId}
                />
            )}
        </>
    );
};

export default OrdableBranchKeys;